import { useEffect, useRef, useState } from 'react';
import { Music, Upload, Trash2, Loader2, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TRACKS = 12;

/**
 * Audio-sample uploader for musicians / producers / beat-makers.
 * - up to 12 tracks, 25 MB each
 * - title auto-filled from filename, editable inline
 * - native <audio controls> player with Range-supported streaming
 */
export default function AudioTracks() {
  const { getAuthHeaders } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const fileRef = useRef(null);

  const resolveUrl = (url) => (url?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/vendors/me`, { headers: getAuthHeaders() });
      if (res.ok) {
        const v = await res.json();
        setTracks(v.audio_tracks || []);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (tracks.length >= MAX_TRACKS) {
      toast.error(`You've reached the ${MAX_TRACKS}-track limit.`);
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File must be under 25 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name.replace(/\.[^.]+$/, ''));
      const res = await fetch(`${API}/vendors/me/audio-track`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        setTracks(prev => [...prev, data]);
        toast.success('Track uploaded');
      } else {
        toast.error(data?.detail || 'Upload failed');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (trackId) => {
    if (!window.confirm('Remove this track from your profile?')) return;
    try {
      const res = await fetch(`${API}/vendors/me/audio-track/${trackId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setTracks(prev => prev.filter(t => t.track_id !== trackId));
        toast.success('Track removed');
      } else {
        const d = await res.json();
        toast.error(d?.detail || 'Delete failed');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const startRename = (track) => {
    setEditingId(track.track_id);
    setEditingTitle(track.title || '');
  };

  const saveRename = async (trackId) => {
    const title = editingTitle.trim().slice(0, 80);
    if (!title) {
      toast.error('Title cannot be empty');
      return;
    }
    try {
      const res = await fetch(`${API}/vendors/me/audio-track/${trackId}/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ title }),
      });
      if (res.ok) {
        setTracks(prev => prev.map(t => t.track_id === trackId ? { ...t, title } : t));
        setEditingId(null);
      } else {
        const d = await res.json();
        toast.error(d?.detail || 'Rename failed');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) return <div className="text-[#9CA3AF] text-sm">Loading audio tracks…</div>;

  return (
    <div className="space-y-4" data-testid="audio-tracks-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-syne text-lg font-bold text-white flex items-center gap-2">
            <Music className="w-4 h-4 text-[#A78BFA]" /> Audio Samples
            <span className="text-xs text-[#9CA3AF] font-outfit font-normal">
              {tracks.length}/{MAX_TRACKS}
            </span>
          </h3>
          <p className="text-xs text-[#64748B] mt-1">
            Upload up to {MAX_TRACKS} tracks (mp3, wav, m4a, ogg, flac — 25 MB max each).
            Buyers hear these directly on your profile.
          </p>
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading || tracks.length >= MAX_TRACKS}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white disabled:opacity-50"
          data-testid="upload-audio-btn"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Uploading…' : 'Upload track'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac"
          onChange={handleUpload}
          className="hidden"
          data-testid="audio-file-input"
        />
      </div>

      {tracks.length === 0 ? (
        <div className="p-8 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] text-center text-[#9CA3AF] text-sm">
          <Music className="w-10 h-10 mx-auto mb-3 text-[#A78BFA]/50" />
          No tracks yet. Upload your first sample — mp3, wav, m4a, ogg, or flac.
        </div>
      ) : (
        <div className="space-y-2">
          {tracks.map((track, idx) => (
            <div
              key={track.track_id}
              className="p-3 rounded-xl bg-white/[0.04] border border-white/10 hover:border-white/20 transition"
              data-testid={`audio-row-${track.track_id}`}
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-8 h-8 shrink-0 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center text-white text-xs font-bold font-mono">
                    {String(idx + 1).padStart(2, '0')}
                  </div>
                  {editingId === track.track_id ? (
                    <div className="flex-1 flex items-center gap-1">
                      <input
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveRename(track.track_id); if (e.key === 'Escape') setEditingId(null); }}
                        className="flex-1 bg-white/5 border border-white/15 rounded-lg px-2 py-1 text-white text-sm"
                        autoFocus
                        maxLength={80}
                        data-testid={`audio-rename-input-${track.track_id}`}
                      />
                      <button onClick={() => saveRename(track.track_id)} className="p-1 text-[#10B981] hover:bg-[#10B981]/15 rounded"><Check size={14} /></button>
                      <button onClick={() => setEditingId(null)} className="p-1 text-[#9CA3AF] hover:bg-white/10 rounded"><X size={14} /></button>
                    </div>
                  ) : (
                    <p className="text-white text-sm font-medium truncate flex-1">{track.title}</p>
                  )}
                </div>
                {editingId !== track.track_id && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => startRename(track)} className="p-1.5 text-[#9CA3AF] hover:text-white hover:bg-white/10 rounded" title="Rename" data-testid={`audio-rename-btn-${track.track_id}`}>
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => handleDelete(track.track_id)} className="p-1.5 text-[#9CA3AF] hover:text-[#FF6B6B] hover:bg-[#FF6B6B]/10 rounded" title="Delete" data-testid={`audio-delete-btn-${track.track_id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
              <audio
                src={resolveUrl(track.url)}
                controls
                preload="metadata"
                className="w-full h-9"
                style={{ filter: 'invert(1) hue-rotate(180deg) brightness(0.9)' }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
